/**
 * Tree projection helpers for surfaces that need a filtered view of
 * the hydrated sheet tree.
 *
 * The hydrated tree remains canonical and complete. Consumers choose
 * a projection for their surface:
 * - property-list.full: expose the whole hydrated tree.
 * - property-list.default: present alias rows as leaves.
 *
 * Surface projection (header / body / footer routing for
 * `inert/projection` rows) is unrelated and lives in
 * `./projection.ts` under the `SurfaceProjection` namespace.
 */

/**
 * How a sheet's rows appear in the property list: the full tree, or the
 * simplified default view.
 */
export namespace PropertyListProjection {
  export type Mode = "full" | "default";

  /**
   * How a row appears in the `default` (simplified) projection. `full`
   * mode ignores this and shows the whole hydrated tree.
   *  - `"branch"`: row shown, children recurse (the normal case).
   *  - `"leaf"`:   row shown, children folded away — e.g. an attached
   *                alias subsuming its template subtree.
   *  - `"omit"`:   row removed entirely — e.g. a source's auto-hydrated
   *                default renderer, plumbing the simplified view hides.
   *
   * Rows declare their own directive (see `ModelRow.defaultProjection`),
   * mirroring `defaultCollapsedInPropertyList`; callers route it through
   * `Deps.defaultProjection` so this module stays tree-shape agnostic.
   */
  export type DefaultProjection = "branch" | "leaf" | "omit";

  export interface Deps<Row> {
    readonly childrenOf: (row: Row) => readonly Row[];
    readonly defaultProjection: (row: Row) => DefaultProjection;
  }

  export interface Projected<Row> {
    readonly row: Row;
    readonly children: readonly Projected<Row>[];
  }

  export function project<Row>(
    rows: readonly Row[],
    mode: Mode,
    deps: Deps<Row>,
  ): readonly Projected<Row>[] {
    const out: Projected<Row>[] = [];
    for (const row of rows) {
      out.push(projectNode(row, mode, deps));
    }
    return out;
  }

  export function projectRows<Row>(
    rows: readonly Row[],
    _mode: Mode,
  ): Row[] {
    return [...rows];
  }

  export function projectChildren<Row>(
    parent: Row,
    children: readonly Row[],
    mode: Mode,
    deps: Pick<Deps<Row>, "defaultProjection">,
  ): Row[] {
    if (mode === "full") return [...children];
    // `default` mode reads each row's own directive: a `"leaf"` parent
    // (e.g. an attached alias) folds its whole subtree away, and any
    // `"omit"` child (e.g. a source's synthetic default renderer) drops
    // out so the parent reads as a clean leaf rather than an expandable
    // node over plumbing.
    if (deps.defaultProjection(parent) === "leaf") return [];
    return children.filter((child) => deps.defaultProjection(child) !== "omit");
  }

  /**
   * Whether `row` presents as a non-expandable leaf in the current mode
   * (its children are folded away). True only in `default` mode for a
   * `"leaf"`-directive row — today that's attached aliases. Drives the
   * drag auto-expand guard.
   */
  export function isAliasLocked<Row>(
    row: Row,
    mode: Mode,
    deps: Pick<Deps<Row>, "defaultProjection">,
  ): boolean {
    return mode === "default" && deps.defaultProjection(row) === "leaf";
  }

  /**
   * A row that carries its own children and default-projection directive:
   * what the `projectSheet*` helpers read (`ModelRow` is one).
   */
  export interface SelfProjecting<Row> {
    /** The row's children, in order. */
    readonly children: readonly Row[];
    /** How the row appears in the `default` projection (`DefaultProjection`). */
    readonly defaultProjection: DefaultProjection;
  }

  export function projectSheetTree<Row extends SelfProjecting<Row>>(
    rows: readonly Row[],
    mode: Mode,
  ): readonly Projected<Row>[] {
    return project(rows, mode, {
      childrenOf: (row) => row.children,
      defaultProjection: ownDirective,
    });
  }

  export function projectSheetRows<Row>(
    rows: readonly Row[],
    mode: Mode,
  ): Row[] {
    return projectRows(rows, mode);
  }

  export function projectSheetChildren<Row extends SelfProjecting<Row>>(
    parent: Row,
    children: readonly Row[],
    mode: Mode,
  ): Row[] {
    return projectChildren(parent, children, mode, OWN_DIRECTIVE);
  }

  export function isSheetAliasLocked<Row extends SelfProjecting<Row>>(
    row: Row,
    mode: Mode,
  ): boolean {
    return isAliasLocked(row, mode, OWN_DIRECTIVE);
  }
}

/** A self-projecting row's own directive (`SelfProjecting.defaultProjection`). */
function ownDirective(row: { readonly defaultProjection: PropertyListProjection.DefaultProjection }): PropertyListProjection.DefaultProjection {
  return row.defaultProjection;
}

const OWN_DIRECTIVE = { defaultProjection: ownDirective };

function projectNode<Row>(
  row: Row,
  mode: PropertyListProjection.Mode,
  deps: PropertyListProjection.Deps<Row>,
): PropertyListProjection.Projected<Row> {
  const children = PropertyListProjection.projectChildren(
    row,
    deps.childrenOf(row),
    mode,
    deps,
  );
  const projectedChildren: PropertyListProjection.Projected<Row>[] = [];
  for (const child of children) {
    projectedChildren.push(projectNode(child, mode, deps));
  }
  return { row, children: projectedChildren };
}
